import {getApiUrl} from '../../../api'
import {getFingerprintId} from './fingerprint'
import {setAccessToken, setAuthUserId} from './tokenStorage'
import {setAuthMode, useTokens, withCredentialsForAuthEndpoints} from './authTransport'
import axios from 'axios'

export type AppError = {
    code: string
    message: string
}
export function normalizeError(error: any): AppError {

    // backend error object
    if (error?.data?.error) {
        return {
            code: error.data.error.code || '',
            message: error.data.error.message || 'Ошибка',
        }
    }

    // backend string
    if (typeof error?.data === 'string') {
        return {
            code: '',
            message: error.data,
        }
    }

    // axios/network/native error
    if (error instanceof Error) {
        return {
            code: '',
            message: error.message,
        }
    }

    // string
    if (typeof error === 'string') {
        return {
            code: '',
            message: error,
        }
    }

    return {
        code: 'GENERAL_ERROR',
        message: 'Что-то пошло не так',
    }
}

let refreshTimer: number | undefined

function onAuthRoute() {
    try { return window.location.pathname.startsWith('/auth') } catch { return false }
}

function scheduleAccessAutoRefresh(expiresAt?: number, skewSec = 45) {
    if (refreshTimer) clearTimeout(refreshTimer)
    if (!expiresAt) return
    // eslint-disable-next-line react-hooks/rules-of-hooks
    if (!useTokens() || onAuthRoute()) return
    const delayMs = Math.max(0, (expiresAt - skewSec) * 1000 - Date.now())
    refreshTimer = window.setTimeout(() => {
        if (!useTokens() || onAuthRoute()) return
        exchangeRefreshToAccess().catch(() => {})
    }, delayMs)
}

type SignInOk =
    | { status: 'ok' }
    | { status: '2fa-required'; method: 'email' | 'totp'; userId: number }

export async function signIn(email: string, password: string): Promise<SignInOk> {
    try {
        const finger_print = (window as any).__APP_FP__ ?? await getFingerprintId()
        const baseUrl = await getApiUrl()

        const { data: signInResp } = await axios.post(
            `${baseUrl}?q=users__sign-in`,
            { object:'users', command:'sign-in', data:{ email, password, finger_print } },
            { headers:{ 'Content-Type':'application/json' }, withCredentials: withCredentialsForAuthEndpoints() }
        )

        if (signInResp.status !== 200) {
            throw signInResp
        }

        const payload = signInResp?.data
        if (payload?.oauth_type === 'none') {

            const { data: accessResp } = await axios.post(
                `${baseUrl}?q=2fa__tokens`,
                { object:'2fa', command:'tokens', data:{ type:'access_token', finger_print } },
                { headers:{ 'Content-Type':'application/json' }, withCredentials: withCredentialsForAuthEndpoints() }
            )

            const { token, expires_at } = accessResp.data
            setAccessToken(token, expires_at)
            setAuthMode('token')
            scheduleAccessAutoRefresh(expires_at)
            return { status: 'ok' }
        }

        if (payload?.oauth_type === 'email' || payload?.oauth_type === 'totp') {
            if (payload.user_id) setAuthUserId(payload.user_id)
            setAuthMode('token')
            return { status:'2fa-required', method: payload.oauth_type, userId: payload.user_id }
        }

        throw {
            data: {
                error: {
                    code: 'GENERAL_ERROR',
                    message: 'Что-то пошло не так',
                },
            },
        }

    } catch (error: any) {

        throw normalizeError(error)
    }
}

export async function submit2faCode(code: string): Promise<void> {

    const finger_print = (window as any).__APP_FP__ ?? await getFingerprintId()
    try {
        const baseUrl = await getApiUrl()

        const { data: refreshResp } = await axios.post(
            `${baseUrl}?q=2fa__tokens`,
            { object:'2fa', command:'tokens', data:{ type:'refresh_token', code, finger_print } },
            { headers:{ 'Content-Type':'application/json' }, withCredentials: withCredentialsForAuthEndpoints() }
        )

        if (refreshResp.status !== 200) {
            throw refreshResp
        }

        if (refreshResp?.status === 200 && refreshResp?.data === true) {
            await exchangeRefreshToAccess({ force: true })
            return
        }

    } catch (error: any) {

        throw normalizeError(error)

    }

}

export async function exchangeRefreshToAccess(opts?: { force?: boolean }): Promise<string> {
    if (!opts?.force) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        if (!useTokens() || onAuthRoute()) throw new Error('refresh disabled (mode is not token)')
    }
    try {
        const finger_print = (window as any).__APP_FP__ ?? await getFingerprintId()
        const baseUrl = await getApiUrl()

        const { data: accessResp } = await axios.post(
            `${baseUrl}?q=2fa__tokens`,
            { object:'2fa', command:'tokens', data:{ type:'access_token', finger_print } },
            { headers:{ 'Content-Type':'application/json' }, withCredentials: withCredentialsForAuthEndpoints() }
        )

        if (accessResp.status !== 200) {
            throw accessResp
        }

        const { token, expires_at } = accessResp.data
        setAccessToken(token, expires_at)
        setAuthMode('token')
        scheduleAccessAutoRefresh(expires_at)
        return token
    } catch (error: any) {
        throw normalizeError(error)
    }

}

export function rescheduleAutoRefresh(exp?: number) {
    scheduleAccessAutoRefresh(exp)
}
